import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  ArchivePostsGroupDocument,
  MyCommunitiesDocument,
  OtherCommunitiesDocument,
  PostsGroupHeaderDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  onSuccessClose?: () => void
  groupId: number
}

function DeleteGroup({ onClose, onSuccessClose, groupId }: Props) {
  const { data, loading } = useQuery(PostsGroupHeaderDocument, {
    variables: {
      id: groupId,
    },
  })

  const group = data?.posts.group

  const [archiveGroup] = useMutation(ArchivePostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.archivePostsGroup,
  })

  const submit = async (closePortal: ClosePortalFn) => {
    if (!group) return

    const result = await archiveGroup({
      variables: {
        id: groupId,
      },
      refetchQueries: [
        {
          query: PostsGroupHeaderDocument,
          variables: { id: groupId },
        },
        {
          query: MyCommunitiesDocument,
        },
        {
          query: OtherCommunitiesDocument,
        },
      ],
    })

    const handled = handleResult(result, (_errors) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.error_message'),
      })
    })

    if (handled.success) {
      if (onSuccessClose) {
        onSuccessClose()
      }
      closePortal()
    }
  }

  if (loading) return <Loading />

  if (!group) return null

  const isDefault = 'default' in group ? group.default : false

  if (isDefault) {
    onClose()
    return null
  }

  return (
    <Modal
      description={i18n.t('dashboard.groups.delete.description')}
      illustration="delete"
      onClose={onClose}
      onSubmit={submit}
      title={i18n.t('dashboard.groups.delete.title')}
      footer={
        <Button type="negative" disabled={!group} submit>
          {i18n.t('dashboard.groups.delete.title')}
        </Button>
      }
    />
  )
}

export default DeleteGroup
